import React, { useCallback, useMemo, useRef } from "react";
import { Checkbox, Dropdown, Form, Input, InputNumber, Select } from "antd";
import {
  DETECTED_FIELD_DATA_TYPE,
  OBJECT_TYPES,
} from "../../../../constants/project";
import { has, pathOr, propOr, values } from "ramda";
import classes from "./object-form-item.module.scss";
import button from "bootstrap/js/src/button";
import { useSelector } from "react-redux";
import { selectJsonTemplateData } from "../../../../reducers/project/selectors";
import { convertMacrosObjectToArray } from "../../../../helpers/common";
const { TextArea } = Input;

const ObjectFormItem = ({
  item,
  objectType,
  data,
  autoFocusFieldRef,
  form,
  defaultTemplateData,
}) => {
  const textAreaForValueRef = useRef(null);
  const jsonTemplateData = useSelector(selectJsonTemplateData);

  const templateArray = useMemo(() => {
    return convertMacrosObjectToArray(JSON.parse(jsonTemplateData));
  }, [jsonTemplateData]);

  const Item = useCallback(
    (type) => {
      switch (type) {
        case "number":
          return (
            <InputNumber
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case "textarea":
          if (item.name === "text") {
            return (
              <TextArea
                rows={item.rows}
                disabled={item.disabled}
                autoFocus={propOr(false, "autoFocus", item)}
                ref={(el) => {
                  if (propOr(false, "autoFocus", item)) {
                    autoFocusFieldRef.current = el;
                  }
                  textAreaForValueRef.current = el;
                }}
              />
            );
          }

          return (
            <TextArea
              rows={item.rows}
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case "string":
          return (
            <Input
              raw={item.raw || false}
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case "boolean":
          return (
            <Checkbox
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case "select":
          return (
            <Select
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            >
              {item.validValues.map((option, index) => (
                <Select.Option value={option.value} key={index}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          );
        case "color":
          return (
            <Input
              type="color"
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );

        default:
          return (
            <Input
              disabled={item.disabled}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
      }
    },
    [item]
  );

  const FieldItem = useCallback(
    (item) => {
      switch (data.type) {
        case DETECTED_FIELD_DATA_TYPE.text:
          if (item.name === "text") {
            return (
              <TextArea
                rows={4}
                disabled={item.disabled}
                autoFocus={propOr(false, "autoFocus", item)}
                ref={(el) => {
                  if (propOr(false, "autoFocus", item)) {
                    autoFocusFieldRef.current = el;
                  }
                  textAreaForValueRef.current = el;
                }}
              />
            );
          }
          return (
            <Input
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case DETECTED_FIELD_DATA_TYPE.checkbox:
          return (
            <Checkbox
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
        case DETECTED_FIELD_DATA_TYPE.combobox: {
          const selectOptions = values(
            has("altItems", data) ? data.altItems : data.items
          );
          return (
            <Select
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            >
              {selectOptions.map((item, index) => (
                <Select.Option key={index} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          );
        }

        case DETECTED_FIELD_DATA_TYPE.listBox: {
          const selectOptions = values(
            has("altItems", data) ? data.altItems : data.items
          );
          return (
            <Select
              mode="multiple"
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            >
              {selectOptions.map((item, index) => (
                <Select.Option key={index} value={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          );
        }
        case DETECTED_FIELD_DATA_TYPE.radioButton:
          return (
            <Input
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );

        default:
          return (
            <Input
              disabled={false}
              autoFocus={propOr(false, "autoFocus", item)}
              ref={propOr(false, "autoFocus", item) ? autoFocusFieldRef : null}
            />
          );
      }
    },
    [data]
  );

  const getValuePropName = useCallback(() => {
    switch (item.type) {
      case "boolean":
        return "checked";
      case "auto": {
        if (data.type === DETECTED_FIELD_DATA_TYPE.checkbox) return "checked";
        return "value";
      }
      default:
        return "value";
    }
  }, [item, data]);

  const isDetectedField = useMemo(() => {
    return objectType === OBJECT_TYPES.DETECTED_FIELD && item.type === "auto";
  }, [item, objectType]);

  const detectedFieldHint = useMemo(() => {
    if (isDetectedField) {
      switch (data.type) {
        case DETECTED_FIELD_DATA_TYPE.radioButton:
          return (
            <div>
              <div>This is a Radio Button. Please:</div>
              enter <strong className="text-primary">0</strong> or
              <strong className="text-primary"> False</strong> for unchecked
              state or <strong className="text-primary">1</strong> or{" "}
              <strong className="text-primary">True</strong> for checked state
            </div>
          );

        default:
          return null;
      }
    }
    return null;
  }, [isDetectedField, data]);

  const macrosMenuItems = useMemo(() => {
    const data = [...defaultTemplateData, ...templateArray];

    return values(data).map((item) => {
      return {
        key: item.label,
        label: item.label,
        onClick: () => {
          const insertedValue =
            propOr(false, "type", item) === "system"
              ? item.value
              : `{{${item.label}}}`;
          const pos =
            textAreaForValueRef.current.resizableTextArea.textArea
              .selectionStart;
          const value =
            textAreaForValueRef.current.resizableTextArea.textArea.value;
          form.setFieldValue(
            "text",
            value.substring(0, pos) + insertedValue + value.substring(pos)
          );
        },
      };
    });
  }, [templateArray, defaultTemplateData]);

  return (
    <>
      {pathOr(null, ["name"], item) === "text" && (
        <div className={classes.InsertMacrosWrap}>
          <Dropdown
            menu={{ items: macrosMenuItems }}
            placement="bottomLeft"
            trigger="click"
            overlayClassName={classes.InsertMacrosMenu}
          >
            <button
              className="btn btn-success btn-sm mb-1 ms-1 me-1"
              type="button"
            >
              Insert Macros
            </button>
          </Dropdown>
        </div>
      )}
      <Form.Item
        label={item.showName ? item.showName : item.name}
        name={item.name}
        disabled={item.disabled}
        rules={item.validation}
        valuePropName={getValuePropName()}
      >
        {isDetectedField ? FieldItem(item) : Item(item.type)}
      </Form.Item>

      {has("hint", item) && (
        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <div className={classes.Hint}>{item.hint}</div>
        </Form.Item>
      )}

      {detectedFieldHint && (
        <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
          <div className={classes.Hint}>{detectedFieldHint}</div>
        </Form.Item>
      )}
    </>
  );
};

export default ObjectFormItem;
